import React, { useState } from "react";
import {
  Button,
  Box,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  Grid,
  Typography,
  CircularProgress,
  Switch,
  Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import VisibilityIcon from "@mui/icons-material/Visibility";

import GenericMUITable from "../../../../components/GenericTable";
import axios from "axios";
import { getOrgCardDetails } from "../../../../utils/api";
import { toast } from "react-toastify";

const OrgProductTable = ({ data, pagination, onToggleDefault }) => {
  const [viewItem, setViewItem] = useState(null);
  const [loadingView, setLoadingView] = useState(false);
  const [togglingId, setTogglingId] = useState(null);

  // -------------------------
  // HANDLE DEFAULT TOGGLE
  // -------------------------
  const handleToggleDefault = async (item) => {
    if (!item?.id) return;

    if (typeof onToggleDefault === "function") {
      try {
        setTogglingId(item.id);
        const res = onToggleDefault(item);
        if (res && typeof res.then === "function") await res;
      } finally {
        setTogglingId(null);
      }
    }
  };

  // -------------------------
  // VIEW DETAILS (API CALL)
  // -------------------------
  const openView = async (item) => {
    if (!item?.id) return;

    setLoadingView(true);
    setViewItem(null);

    try {
      const res = await getOrgCardDetails({ id: item.id });
      const data = res?.data ?? res;

      if (data?.code === "0000" && data?.data) {
        setViewItem(data.data);
      } else {
        toast.error(data?.msg || "Failed to fetch card details", {
          position: "top-right",
        });
      }
    } catch (error) {
      console.error("Error fetching card details:", error);

      toast.error(error?.response?.data?.msg || "Network error fetching card details", { position: "top-right" });
    } finally {
      setLoadingView(false);
    }
  };

  const closeView = () => setViewItem(null);

  // -------------------------
  // TABLE FIELDS
  // -------------------------
  const tableFields = [
    // { key: "id", label: "UUID" },
    { key: "productName", label: "Product Name" },
    { key: "productCode", label: "Product Code" },
    { key: "orgCode", label: "Org Code" },
    {
      key: "status",
      label: "Status",
      render: (item) => item?.status?.toUpperCase() || "-",
    },
    {
      key: "actions",
      label: "Actions",
      render: (item) => (
        <Button
          size="small"
          onClick={() => openView(item)}
          aria-label={`View ${item.productCode}`}
          sx={{ minWidth: "auto", p: "4px" }}
        >
          <VisibilityIcon sx={{ color: "#828282" }} />
        </Button>
      ),
    },
  ];

  return (
    <>
      <GenericMUITable fields={tableFields} data={data} rowKey="id" pagination={pagination} />

      {/* VIEW POPUP */}
      <Dialog open={!!viewItem || loadingView} onClose={closeView} maxWidth="md" fullWidth>
        <DialogTitle
          sx={{
            fontWeight: 600,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          Product Details
          {!loadingView && (
            <IconButton onClick={closeView}>
              <CloseIcon />
            </IconButton>
          )}
        </DialogTitle>

        <Divider />

        <DialogContent sx={{ p: 3 }}>
          {loadingView && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
              <CircularProgress />
            </Box>
          )}

          {viewItem && !loadingView && (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2">UUID</Typography>
                <Typography sx={{ mb: 2 }}>{viewItem.id}</Typography>

                <Typography variant="subtitle2">Product Name</Typography>
                <Typography sx={{ mb: 2 }}>{viewItem.productName || "-"}</Typography>

                <Typography variant="subtitle2">Product Code</Typography>
                <Typography sx={{ mb: 2 }}>{viewItem.productCode}</Typography>

                <Typography variant="subtitle2">BIN</Typography>
                <Typography sx={{ mb: 2 }}>{viewItem.bin || "-"}</Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2">Status</Typography>
                <Typography sx={{ mb: 2 }}>{viewItem.status}</Typography>

                <Typography variant="subtitle2">Default</Typography>
                <Typography sx={{ mb: 2 }}>{viewItem.default ? "Yes" : "No"}</Typography>

                <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2">Front Image</Typography>
                    {viewItem.frontImage ? (
                      <Box
                        component="img"
                        src={viewItem.frontImage}
                        alt="front"
                        sx={{ width: "100%", maxHeight: 140, objectFit: "contain", mt: 1 }}
                      />
                    ) : (
                      "-"
                    )}
                  </Box>

                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2">Back Image</Typography>
                    {viewItem.backImage ? (
                      <Box
                        component="img"
                        src={viewItem.backImage}
                        alt="back"
                        sx={{ width: "100%", maxHeight: 140, objectFit: "contain", mt: 1 }}
                      />
                    ) : (
                      "-"
                    )}
                  </Box>
                </Box>
              </Grid>
            </Grid>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default OrgProductTable;
